/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import api from "common/utils/api";
import { objNamesToCamelCase, objNamesToSnakeCase } from "common/utils/helpers/cases";
import type { Clarification, PinState } from "./pinning-types";

const MAX_MESSAGE_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

export type SubmitPinMessageParams = {
  sessionId: string;
  pinId: string;
  coordinates: { x: number; y: number };
  userMessage: string;
  isFirstMessage: boolean;
  priorPins: Array<{
    pinId: string;
    coordinates: { x: number; y: number };
    state: string;
    instruction: string | { instruction: string } | undefined;
  }>;
};

export type PinMessageResult = {
  status: string;
  message?: string;
  instruction?: string;
  clarifications?: Clarification[];
  generatedImageUrl?: string;
  aoiResult?: Record<string, unknown>;
  text?: Record<string, unknown>;
};

export type SubmitPinMessageHandlers = {
  onAcknowledgment: (payload: { message?: string }) => void;
  onResult: (payload: PinMessageResult) => void;
  onError: (error: Error) => void;
  onStreamEnd?: () => void;
};

export type SubmitPinMessageOptions = {
  isPinActive?: (pinId: string) => boolean;
  signal?: AbortSignal;
};

function parseSseEventChunk(chunk: string): { event: string | undefined; data: unknown } | null {
  const lines = chunk
    .split(/\r?\n/)
    .map((l: string) => l.trimEnd())
    .filter((l: string) => l.length > 0 && !l.startsWith(":"));

  if (lines.length === 0) return null;

  let event: string | undefined;
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trim();
      // eslint-disable-next-line no-continue
      continue;
    }
    if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trimStart());
    }
  }

  const dataString = dataLines.join("\n");
  if (!dataString) return null;

  try {
    return { event, data: JSON.parse(dataString) };
  } catch {
    return { event, data: dataString };
  }
}

// eslint-disable-next-line max-lines-per-function
async function attemptPinMessage(
  params: SubmitPinMessageParams,
  handlers: SubmitPinMessageHandlers,
  { signal }: { signal?: AbortSignal } = {}
): Promise<boolean> {
  const response = await fetch(`/api/pin_sessions/${params.sessionId}/message`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    signal: signal ?? null,
    body: JSON.stringify({
      pin_id: params.pinId,
      coordinates: params.coordinates,
      user_message: params.userMessage,
      is_first_message: params.isFirstMessage,
      prior_pins: params.priorPins.map(p => ({
        pin_id: p.pinId,
        coordinates: p.coordinates,
        state: p.state,

        instruction:
          typeof p.instruction === "string" ? p.instruction : (p.instruction?.instruction ?? "")
      }))
    })
  });

  if (!response.ok) {
    const error = new Error(`Failed to submit pin message (${response.status})`);
    if (response.status >= 400 && response.status < 500) {
      error.name = "ClientError";
    }
    throw error;
  }

  if (!response.body) {
    throw new Error("Missing streaming response body");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split(/\r?\n\r?\n/);
    buffer = parts.pop() ?? "";

    for (const part of parts) {
      const evt = parseSseEventChunk(part);
      // eslint-disable-next-line no-continue
      if (!evt) continue;

      if (evt.event === "acknowledgment") {
        handlers.onAcknowledgment(objNamesToCamelCase(evt.data) as { message?: string });
      } else if (evt.event === "result") {
        const result = objNamesToCamelCase(evt.data) as PinMessageResult;
        handlers.onResult(result);
        // Continue reading after "ready" — the backend may send a follow-up
        // "preview_ready" event after synchronous image generation completes.
        if (result.status !== "ready") return false;
      } else if (evt.event === "error") {
        const errorPayload = objNamesToCamelCase(evt.data) as { error?: string };
        throw new Error(errorPayload?.error || "Server error");
      }
    }
  }

  // Stream ended after processing a "ready" result (waited for potential preview_ready)
  return true;
}

/**
 * Submits a pin message with SSE streaming and retry logic.
 *
 * @param params - { sessionId, pinId, coordinates, userMessage, isFirstMessage, priorPins }
 * @param handlers - { onAcknowledgment, onResult, onError }
 * @param options - { isPinActive?: (pinId: string) => boolean, signal?: AbortSignal }
 */
export async function submitPinMessage(
  params: SubmitPinMessageParams,
  handlers: SubmitPinMessageHandlers,
  options: SubmitPinMessageOptions = {}
): Promise<void> {
  const { isPinActive, signal } = options;
  let lastError = new Error("Unknown error");

  for (let attempt = 1; attempt <= MAX_MESSAGE_RETRIES; attempt += 1) {
    if (signal?.aborted) return;
    if (typeof isPinActive === "function" && !isPinActive(params.pinId)) return;

    try {
      // eslint-disable-next-line no-await-in-loop
      const hadReadyResult = await attemptPinMessage(params, handlers, signal ? { signal } : {});
      if (hadReadyResult) handlers.onStreamEnd?.();
      return;
    } catch (e) {
      if (signal?.aborted) return;
      lastError = e instanceof Error ? e : new Error("Unknown error");

      if (lastError.name === "ClientError") {
        handlers.onError(lastError);
        return;
      }

      if (attempt < MAX_MESSAGE_RETRIES) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => {
          setTimeout(resolve, RETRY_DELAY_MS * attempt);
        });
      }
    }
  }

  handlers.onError(lastError);
}

/**
 * Creates a new pinning session.
 *
 * @param artworkId
 * @param options
 * @returns Session data with sessionId
 */
export async function createSession(
  artworkId: string,
  { previewImageName }: { previewImageName?: string | undefined } = {}
): Promise<Record<string, unknown>> {
  const params: Record<string, string> = { artworkId };
  if (previewImageName) {
    params["previewImageName"] = previewImageName;
  }
  const data = await api.designLab.pinning.createSession(objNamesToSnakeCase(params));
  return objNamesToCamelCase(data) as unknown as Record<string, unknown>;
}

/**
 * Fetches existing pins for a session.
 *
 * @param sessionId
 * @returns Pins array and generated image URL
 */
export async function fetchPins(
  sessionId: string
): Promise<{ pins: Array<Record<string, unknown>>; generatedImageUrl: string | null }> {
  const data = await api.designLab.pinning.getPins({ id: sessionId });
  const pins = Array.isArray(data.pins) ? data.pins : [];
  return {
    pins: pins.map(p => objNamesToCamelCase(p)) as Array<Record<string, unknown>>,
    generatedImageUrl: data.generated_image_url ?? null
  };
}

/**
 * Completes a pinning session.
 *
 * @param sessionId
 * @returns Session completion data
 */
export async function completeSession(sessionId: string): Promise<Record<string, unknown>> {
  const data = await api.designLab.pinning.completeSession(objNamesToSnakeCase({ id: sessionId }));
  return objNamesToCamelCase(data) as unknown as Record<string, unknown>;
}

/**
 * Cancels a pinning session.
 *
 * @param sessionId
 * @returns Session cancellation data
 */
export async function cancelSession(sessionId: string): Promise<Record<string, unknown>> {
  const data = await api.designLab.pinning.cancelSession(objNamesToSnakeCase({ id: sessionId }));
  return objNamesToCamelCase(data) as unknown as Record<string, unknown>;
}

/**
 * Removes a pin from a session.
 *
 * @param sessionId
 * @param pinId
 * @returns Removal result
 */
export async function removePin(sessionId: string, pinId: string): Promise<unknown> {
  return api.designLab.pinning.removePin({ id: sessionId, pinId });
}

/**
 * Updates the instruction text of a pin.
 *
 * @param sessionId
 * @param pinId
 * @param instruction
 * @returns Update result
 */
export async function updatePinInstruction(
  sessionId: string,
  pinId: string,
  instruction: string
): Promise<Record<string, unknown>> {
  const data = await api.designLab.pinning.updatePinInstruction(
    { id: sessionId, pinId },
    objNamesToSnakeCase({ instruction })
  );
  return objNamesToCamelCase(data) as unknown as Record<string, unknown>;
}

/**
 * Bulk-deletes pins from a session.
 *
 * @param sessionId
 * @param pinIds
 */
export async function bulkDeletePins(sessionId: string, pinIds: string[]): Promise<void> {
  await api.designLab.pinning.bulkDeletePins({ id: sessionId }, objNamesToSnakeCase({ pinIds }));
}

/**
 * Submits feedback for a pinning session.
 *
 * @param sessionId
 * @param feedback - { positive, message, currentPin, currentPinState }
 */
export async function submitFeedback(
  sessionId: string,
  feedback: {
    positive: boolean;
    message: string | null;
    currentPin: string | null;
    currentPinState: PinState | null;
  }
): Promise<void> {
  await api.designLab.pinning.createFeedback({ id: sessionId }, objNamesToSnakeCase({ feedback }));
}

/**
 * Approves a pin (and completes the session).
 *
 * @param sessionId
 * @returns Approval result
 */
export async function approvePin(sessionId: string): Promise<Record<string, unknown>> {
  const data = await api.designLab.pinning.approvePin({ id: sessionId });
  return objNamesToCamelCase(data) as unknown as Record<string, unknown>;
}

/**
 * Rejects a pin (sends to designers, completes the session).
 *
 * @param sessionId
 * @returns Rejection result
 */
export async function rejectPin(sessionId: string): Promise<Record<string, unknown>> {
  const data = await api.designLab.pinning.rejectPin({ id: sessionId });
  return objNamesToCamelCase(data) as unknown as Record<string, unknown>;
}

/**
 * Retries image generation for a recovered session with a ready pin.
 *
 * @param sessionId
 * @returns Retry result
 */
export async function retryPreview(sessionId: string): Promise<Record<string, unknown>> {
  const data = await api.designLab.pinning.retryPreview({ id: sessionId });
  return objNamesToCamelCase(data) as unknown as Record<string, unknown>;
}
